import {  } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../Store';
import { Api } from '../../Api/Api';
import { IProductGamePlanDetails, IProductPlanRequest } from '../../Models/Responses/ProductGamePlan';
import { getCurrentActingUser } from '../User/AuthSlice';
import { IFieldlessAllocationResponse, IFieldlessAllocationItemResponse, IFieldlessRankingSeed } from '../../Models/Responses/FieldlessSeedRankingResponse';
import { createTracedAsyncThunk } from '../../../tracing/trace';

/**
 * Create a new product plan.
 * 
 * POST /growers/{id}/years/{cropYear}/productplans
 */
export const createProductPlan = createTracedAsyncThunk<IProductGamePlanDetails, {
	growerId:string,
	body: IProductPlanRequest,
}, { rejectValue: string, dispatch: AppDispatch; state: RootState; }>(
	'productGamePlanV7/createProductPlan',
	async (context, args, thunkAPI) =>
	{
		const { growerId, body, } = args;
		const { auth, crops } = thunkAPI.getState() as RootState;

		const api = new Api('/api/6', auth.userAuthToken, context);

		try
		{
			const createResponse = await api.postAsync<string>(`growers/${growerId}/years/${crops.CropYear}/productplans`, {});

			if (!createResponse.Success)
			{
				return thunkAPI.rejectWithValue(createResponse.ErrorMessage);
			}

			const planId = createResponse.Data;

			// Create does not take any data so use the PUT immediately afterwards.
			const updateResponse = await api.putAsync<IProductGamePlanDetails>(`productplans/${planId}/products`, body);

			if (!updateResponse.Success)
			{
				return thunkAPI.rejectWithValue(updateResponse.ErrorMessage);
			}

			return updateResponse.Data;
		}
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/** 
 * Update an existing product plan.
 * 
 * PUT /productplans/{id}/products
 */
export const putProductPlan = createTracedAsyncThunk<IProductGamePlanDetails, {
	planId: string,
	body: IProductPlanRequest,
}, { rejectValue: string, dispatch: AppDispatch; state: RootState; }>(
	'productGamePlanV7/putProductPlan',
	async (context, args, thunkAPI) =>
	{
		const { planId, body, } = args;
		const { auth } = thunkAPI.getState() as RootState;

		const api = new Api('/api/6', auth.userAuthToken, context);

		try
		{
			const response = await api.putAsync<IProductGamePlanDetails>(`productplans/${planId}/products`, body);

			if (!response.Success)
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}

			return response.Data;
		}
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Download a specific identified product plan
 */
export const getProductGamePlan = 
createTracedAsyncThunk<IProductGamePlanDetails & { defaultTreatmentName: string; growerZip: string; }, { planId: string; defaultTreatmentName: string; }, { dispatch: AppDispatch; state: RootState; }>(
	'productGamePlan/get',
	async (context, { planId, defaultTreatmentName }, thunkAPI) =>
	{
		try
		{
			const currentState = thunkAPI.getState();
			const selectedGrowerId = currentState.ui.SelectedGrowerId;
			let growerZip = '';
			if (selectedGrowerId)
			{
				const selectedGrower = currentState.grower.Growers.find(g => g.Id === selectedGrowerId);
				growerZip = selectedGrower?.Zip;
			}
			const api = new Api('/api/6', currentState.auth.userAuthToken, context);
			const response = await api.getAsync<IProductGamePlanDetails>(`productplans/${planId}`);

			if (response.ErrorCode === null && response.Success)
			{
				return { ...response.Data, defaultTreatmentName, growerZip };
			}
			else
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export interface IFieldlessSeedRankingsRequest
{
	acres: number;
	cropAssociation: string;
	consistencyPerf: number;
	cropId: string;
	enogenOnly: boolean;
	fungicide: boolean;
	irrigated: boolean;
	isFilterApplied: boolean;
	rmMax: number;
	rmMin: number;
	seedsRequested?: number;
	selectedTraits: string[];
	topEndYield: number;
	zipCode: string;
}

export const getFieldlessSeedAllocations = createTracedAsyncThunk<IFieldlessAllocationResponse, IFieldlessSeedRankingsRequest, { dispatch: AppDispatch; state: RootState; }>(
	'fieldlessallocations/get',
	async (context, request: IFieldlessSeedRankingsRequest, thunkAPI) =>
	{
		try
		{
			const { 
				acres,
				cropId,
				enogenOnly,
				irrigated,
				seedsRequested,
				fungicide,
				isFilterApplied,
				rmMax,
				rmMin,
				selectedTraits,
				topEndYield,
				consistencyPerf,
				zipCode
			} = request;
			const currentState = thunkAPI.getState();
			const currentUser = getCurrentActingUser(currentState);
			const api = new Api('/api/6', currentState.auth.userAuthToken, context);
			const rankingRequest = 
			{
				'Acres': acres,
				'ConsistencyPerf': consistencyPerf,
				'EnogenOnly': enogenOnly,
				'Fungicide': fungicide,
				'Irrigated': irrigated,
				'IsFilterApplied': isFilterApplied,
				'RmMax': rmMax,
				'RmMin': rmMin,
				'SelectedTraits': selectedTraits,
				'TopEndYield': topEndYield,
				'TotalRequestedSeeds': seedsRequested,
			};
			const response = await api.postAsync<IFieldlessAllocationItemResponse[]>(
				`users/${currentUser.UserId}/crops/${cropId}/zipCodes/${zipCode}/recommendation`, rankingRequest);

			if (response.ErrorCode === null && response.Success)
			{
				return {
					IsEnogen: enogenOnly,
					Items: response.Data
				};
			}
			else
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export const getFieldlessSeedRankings = createTracedAsyncThunk<{ cropAssociation: string, rankings: IFieldlessRankingSeed[] }, IFieldlessSeedRankingsRequest, { dispatch: AppDispatch; state: RootState; }>(
	'fieldlessrankings/get',
	async (context, request: IFieldlessSeedRankingsRequest, thunkAPI) =>
	{
		try
		{
			const {
				acres,
				cropId,
				cropAssociation,
				enogenOnly,
				irrigated,
				isFilterApplied,
				fungicide,
				rmMax,
				rmMin,
				selectedTraits,
				topEndYield,
				consistencyPerf,
				zipCode
			} = request;
			const currentState = thunkAPI.getState();
			const currentUser = getCurrentActingUser(currentState);
			const api = new Api('/api/6', currentState.auth.userAuthToken, context);
			const rankingRequest = 
			{
				'Acres': acres,
				'ConsistencyPerf': consistencyPerf,
				'EnogenOnly': enogenOnly,
				'Fungicide': fungicide,
				'Irrigated': irrigated,
				'IsFilterApplied': isFilterApplied,
				'RmMax': rmMax,
				'RmMin': rmMin,
				'SelectedTraits': selectedTraits,
				'TopEndYield': topEndYield,
			};
			const response = await api.postAsync<IFieldlessRankingSeed[]>(
				`users/${currentUser.UserId}/crops/${cropId}/zipCodes/${zipCode}/filterallseeds`, rankingRequest);

			if (response.ErrorCode === null && response.Success)
			{
				return { cropAssociation: cropAssociation, rankings: response.Data };
			}
			else
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);